import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Pencil, Trash2, CalendarClock } from "lucide-react";
import CampAgendaManager from "./CampAgendaManager";

const TYPES = ["ID Camp", "Showcase", "Selection Camp", "Clinic", "Tryout", "CBR Nation Practice", "CBR Nation Tournament", "CBR Nation Travel Event", "CBR Nation Team Meeting"];
const TIMEZONES = ["America/Toronto", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles"];
const PROVINCES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];

const EMPTY_FORM = {
  title: "",
  type: "ID Camp",
  description: "",
  start_date: "",
  end_date: "",
  venue: "",
  city: "",
  province: "ON",
  price: 0,
  currency: "CAD",
  capacity: 50,
  registration_deadline: "",
  cover_image_url: "",
  age_groups: "",
  timezone: "America/Toronto",
  arrival_time: "",
  checkin_time: "",
  status: "open",
};

export default function AdminCamps() {
  const [camps, setCamps] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [agendaCamp, setAgendaCamp] = useState(null);

  const load = () => api.get("/camps", { params: { status_filter: "" } }).then(({ data }) => setCamps(data.items || []));
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setError("");
    setShowForm(true);
  };
  const openEdit = (c) => {
    setForm({
      ...EMPTY_FORM,
      ...c,
      start_date: c.start_date?.slice(0, 16) || "",
      end_date: c.end_date?.slice(0, 16) || "",
      registration_deadline: c.registration_deadline?.slice(0, 16) || "",
      age_groups: (c.age_groups || []).join(", "),
    });
    setEditingId(c.id);
    setError("");
    setShowForm(true);
  };

  const save = async () => {
    setError("");
    setBusy(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        capacity: Number(form.capacity),
        age_groups: form.age_groups
          ? form.age_groups.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
        registration_deadline: form.registration_deadline
          ? new Date(form.registration_deadline).toISOString()
          : null,
      };
      // Agenda is managed exclusively in the Agenda & Comms manager — never let the
      // main camp form overwrite it with stale data.
      delete payload.agenda;
      if (editingId) {
        await api.patch(`/camps/${editingId}`, payload);
      } else {
        await api.post("/camps", payload);
      }
      setShowForm(false);
      await load();
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this camp permanently?")) return;
    await api.delete(`/camps/${id}`);
    load();
  };

  const sendReminders = async (c) => {
    if (!confirm(`Send reminder emails to all registered players & parents for "${c.title}"?`)) return;
    try {
      const { data } = await api.post(`/admin/camps/${c.id}/send-reminders`);
      toast.success(`${data.recipients} reminder email(s) ${data.live ? "sent" : "queued (email mocked)"}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not send reminders");
    }
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-7xl">
        <div className="flex items-end justify-between">
          <div>
            <div className="label-overline text-[#E63946]">Camps</div>
            <h1 data-testid="admin-camps-title" className="font-display text-4xl uppercase tracking-tighter mt-3">Camp Management</h1>
          </div>
          <button
            data-testid="admin-new-camp-button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-5 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm"
          >
            <Plus className="w-4 h-4" /> New Camp
          </button>
        </div>

        <div className="mt-8 bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left p-4 label-overline">Title</th>
                <th className="text-left p-4 label-overline">Type</th>
                <th className="text-left p-4 label-overline">Date</th>
                <th className="text-left p-4 label-overline">Location</th>
                <th className="text-left p-4 label-overline">Price</th>
                <th className="text-left p-4 label-overline">Status</th>
                <th className="text-right p-4 label-overline">Actions</th>
              </tr>
            </thead>
            <tbody>
              {camps.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 last:border-0">
                  <td className="p-4 font-semibold">{c.title}</td>
                  <td className="p-4">{c.type}</td>
                  <td className="p-4 font-data">{new Date(c.start_date).toLocaleDateString("en-CA")}</td>
                  <td className="p-4">{c.city}, {c.province}</td>
                  <td className="p-4 font-data">${c.price}</td>
                  <td className="p-4">
                    <span className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider bg-slate-100">
                      {c.status}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="inline-flex gap-2">
                      <Link
                        data-testid={`ops-camp-${c.id}`}
                        to={`/admin/camps/${c.id}/live`}
                        className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider bg-[#0A192F] text-white hover:bg-[#0A192F]/90 rounded-sm"
                      >
                        Camp Ops
                      </Link>
                      <Link
                        data-testid={`checkin-camp-${c.id}`}
                        to={`/admin/camps/${c.id}/checkin`}
                        className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider bg-[#E63946] text-white hover:bg-[#D90429] rounded-sm"
                      >
                        Check-In
                      </Link>
                      <button
                        data-testid={`agenda-comms-${c.id}`}
                        onClick={() => setAgendaCamp(c)}
                        className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm flex items-center gap-1"
                      >
                        <CalendarClock className="w-3.5 h-3.5" /> Agenda & Comms
                      </button>
                      <button
                        data-testid={`send-reminders-${c.id}`}
                        onClick={() => sendReminders(c)}
                        className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider border border-slate-300 text-[#0A192F] hover:bg-slate-50 rounded-sm"
                      >
                        Send Reminders
                      </button>
                      <button data-testid={`edit-camp-${c.id}`} onClick={() => openEdit(c)} className="p-2 hover:bg-slate-100 rounded-sm">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button data-testid={`delete-camp-${c.id}`} onClick={() => remove(c.id)} className="p-2 hover:bg-slate-100 rounded-sm text-[#E63946]">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {camps.length === 0 && (
                <tr><td colSpan="7" className="p-10 text-center text-sm text-slate-500">No camps yet. Create one to get started.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {agendaCamp && (
        <CampAgendaManager camp={agendaCamp} camps={camps} onClose={() => setAgendaCamp(null)} onSaved={load} />
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4">
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-sm">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 sticky top-0 bg-white">
              <h2 className="font-display text-2xl uppercase tracking-tight">
                {editingId ? "Edit Camp" : "Create Camp"}
              </h2>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {error && <div className="border-l-4 border-[#E63946] bg-red-50 p-3 text-sm text-[#E63946]">{error}</div>}

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="label-overline">Title</label>
                  <input data-testid="camp-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm" />
                </div>
                <div>
                  <label className="label-overline">Type</label>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white">
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-overline">Status</label>
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white">
                    <option value="draft">Draft</option>
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div>
                  <label className="label-overline">Timezone</label>
                  <select data-testid="camp-form-timezone" value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white">
                    {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-overline">Arrival Time</label>
                  <input data-testid="camp-form-arrival" type="time" value={form.arrival_time || ""} onChange={(e) => setForm({ ...form, arrival_time: e.target.value })} className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm" />
                </div>
                <div>
                  <label className="label-overline">Check-In Time</label>
                  <input data-testid="camp-form-checkin" type="time" value={form.checkin_time || ""} onChange={(e) => setForm({ ...form, checkin_time: e.target.value })} className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label-overline">Description</label>
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows="3" className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm" />
                </div>
                <div>
                  <label className="label-overline">Start Date/Time</label>
                  <input data-testid="camp-form-start-date" type="datetime-local" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm" />
                </div>
                <div>
                  <label className="label-overline">End Date/Time</label>
                  <input data-testid="camp-form-end-date" type="datetime-local" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm" />
                </div>
                <div>
                  <label className="label-overline">Venue</label>
                  <input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm" />
                </div>
                <div>
                  <label className="label-overline">City</label>
                  <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm" />
                </div>
                <div>
                  <label className="label-overline">Province</label>
                  <select value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white">
                    {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-overline">Capacity</label>
                  <input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm" />
                </div>
                <div>
                  <label className="label-overline">Price (CAD)</label>
                  <input data-testid="camp-form-price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm" />
                </div>
                <div>
                  <label className="label-overline">Registration Deadline</label>
                  <input type="datetime-local" value={form.registration_deadline} onChange={(e) => setForm({ ...form, registration_deadline: e.target.value })} className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label-overline">Age Groups (comma separated grad years)</label>
                  <input value={form.age_groups} onChange={(e) => setForm({ ...form, age_groups: e.target.value })} placeholder="2026, 2027, 2028" className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label-overline">Cover Image URL</label>
                  <input value={form.cover_image_url} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })} className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-sm" />
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setShowForm(false)} className="px-5 py-2 border border-slate-300 text-sm font-semibold uppercase tracking-wider rounded-sm">
                Cancel
              </button>
              <button
                data-testid="camp-form-save-button"
                onClick={save}
                disabled={busy}
                className="px-5 py-2 bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60"
              >
                {busy ? "Saving…" : editingId ? "Update Camp" : "Create Camp"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
